import {
  Avatar,
  Badge,
  Box,
  Button,
  Container,
  Flex,
  Heading,
  HStack,
  Icon,
  Link,
  Separator,
  Text,
  VStack,
} from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { FiCalendar, FiExternalLink, FiPaperclip } from "react-icons/fi";
import { useNavigate, useParams } from "react-router-dom";
import { useGetNewsMediaPublicationQuery } from "../../features/cmsApi";
import BreadcrumbComponent from "./BreadcrumbComponent";

interface NewsItem {
  id: string | number;
  title: string;
  content:
    | Array<{
        type: string;
        children: Array<{
          text: string;
          type: string;
        }>;
      }>
    | string;
  category: string | null;
  image?: string;
  thumbnail?: {
    url: string;
    formats?: {
      thumbnail?: {
        url: string;
      };
      small?: {
        url: string;
      };
      medium?: {
        url: string;
      };
    };
  };
  publishedDate: string;
  attachment?: string;
  link?: string | null;
  status?: string;
}

function extractTextFromContent(content: NewsItem["content"]): string {
  if (typeof content === "string") {
    return content;
  }

  return content
    .map((paragraph) => paragraph.children.map((child) => child.text).join(""))
    .join("\n\n")
    .trim();
}

function NewsDetails() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data } = useGetNewsMediaPublicationQuery();
  const [newsItem, setNewsItem] = useState<NewsItem | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (data?.data?.news && id) {
      const foundNews = data.data.news.find(
        (item: NewsItem) => item.id.toString() === id
      );
      setNewsItem(foundNews || null);
      setLoading(false);
    }
  }, [data, id]);

  const handleGoBack = () => {
    navigate("/news");
  };

  if (loading) {
    return (
      <Container maxW="4xl" py={12}>
        <Box textAlign="center" py={20}>
          <Text fontSize="lg" color="gray.500">
            Loading news details...
          </Text>
        </Box>
      </Container>
    );
  }

  if (!newsItem) {
    return (
      <Container maxW={"4xl"} py={12}>
        <Box textAlign="center" py={20}>
          <Text fontSize="6xl" opacity={0.3} mb={6}>
            📰
          </Text>
          <Heading size="lg" color="gray.600" mb={4}>
            News Not Found
          </Heading>
          <Text color="gray.500" mb={8}>
            The news article you're looking for doesn't exist or has been
            removed.
          </Text>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxW={"4xl"} py={8}>
      {/* Breadcrumb and Back Button */}
      <Box mb={8}>
        <Flex justify="space-between" align="center" mb={4}>
          <BreadcrumbComponent
            data={[
              { title: "Home", href: "/" },
              { title: "News", href: "/news" },
              { title: newsItem.title.substring(0, 20) + "...", href: null },
            ]}
          />
        </Flex>
      </Box>

      {/* News Article */}
      <Box
        bg="white"
        borderRadius="2xl"
        overflow="hidden"
        boxShadow="xl"
        border="1px"
        borderColor="gray.200"
      >
        {/* Title Section */}
        <Box px={{ base: 6, md: 12 }} pt={{ base: 8, md: 12 }} pb={6}>
          <VStack alignItems="start" gap={4}>
            <Heading
              size={{ base: "lg", md: "xl", lg: "2xl" }}
              lineHeight="1.2"
              color="#090047"
              fontWeight="bold"
            >
              {newsItem.title}
            </Heading>

            <HStack gap={6} flexWrap="wrap">
              <Badge
                colorScheme={newsItem.category ? "blue" : "gray"}
                variant="subtle"
                px={4}
                py={2}
                borderRadius="full"
                fontSize="sm"
                fontWeight="semibold"
              >
                {newsItem.category || "General"}
              </Badge>

              <HStack color="gray.500" fontSize="sm">
                <Icon>
                  <FiCalendar />
                </Icon>
                <Text fontWeight="medium">
                  {new Date(newsItem.publishedDate).toLocaleDateString(
                    "en-US",
                    {
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                    }
                  )}
                </Text>
              </HStack>
            </HStack>
          </VStack>
        </Box>

        {newsItem?.thumbnail?.url ? (
          <Avatar.Root
            shape="square"
            w="100%"
            h={{ base: "180px", md: "200px", lg: "220px" }}
            borderTopRadius="xl"
            bg="gray.50"
          >
            <Avatar.Fallback
              name={newsItem.title}
              bg="gray.100"
              color="gray.500"
            />
            <Avatar.Image
              src={`${import.meta.env.VITE_CMS_API_URL}${
                newsItem?.thumbnail?.url
              }`}
              objectFit="contain"
              w="100%"
              h="100%"
            />
          </Avatar.Root>
        ) : (
          <Avatar.Root
            shape="square"
            w="100%"
            h={{ base: "180px", md: "200px", lg: "220px" }}
            bg="gray.100"
            borderTopRadius="xl"
          >
            <Avatar.Fallback
              name={newsItem.title}
              bg="gray.100"
              color="gray.400"
              w="100%"
              h="100%"
            >
              <Text fontSize="4xl">📰</Text>
            </Avatar.Fallback>
          </Avatar.Root>
        )}
        <Separator />

        {/* Content Section */}
        <Box px={{ base: 6, md: 12 }} py={{ base: 8, md: 12 }}>
          <Text
            fontSize={{ base: "md", md: "lg" }}
            lineHeight="1.8"
            color="gray.700"
            whiteSpace="pre-line"
            mb={8}
            textAlign="justify"
          >
            {extractTextFromContent(newsItem.content)}
          </Text>

          {/* Action Buttons */}
          {(newsItem.attachment || newsItem.link) && (
            <>
              <Separator mb={8} />
              <VStack alignItems="start" gap={4}>
                <Text fontSize="lg" fontWeight="semibold" color="#090047">
                  Additional Resources
                </Text>
                <HStack gap={4} flexWrap="wrap">
                  {newsItem.attachment && (
                    <Link
                      href={newsItem.attachment}
                      target="_blank"
                      rel="noopener noreferrer"
                      _hover={{ textDecoration: "none" }}
                    >
                      <Button
                        colorScheme="blue"
                        variant="solid"
                        borderRadius="full"
                        size="md"
                        _hover={{
                          bg: "blue.600",
                          transform: "translateY(-2px)",
                        }}
                        transition="all 0.2s"
                      >
                        <Icon mr={2}>
                          <FiPaperclip />
                        </Icon>
                        Download Attachment
                      </Button>
                    </Link>
                  )}

                  {newsItem.link && (
                    <Link
                      href={newsItem.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      _hover={{ textDecoration: "none" }}
                    >
                      <Button
                        colorScheme="teal"
                        variant="outline"
                        borderRadius="full"
                        size="md"
                        _hover={{
                          bg: "teal.50",
                          borderColor: "teal.300",
                          transform: "translateY(-2px)",
                        }}
                        transition="all 0.2s"
                      >
                        <Icon mr={2}>
                          <FiExternalLink />
                        </Icon>
                        Visit External Link
                      </Button>
                    </Link>
                  )}
                </HStack>
              </VStack>
            </>
          )}
        </Box>

        {/* Footer */}
        <Box bg="gray.50" px={{ base: 6, md: 12 }} py={6}>
          <Flex justify="space-between" align="center" flexWrap="wrap" gap={4}>
            <Text fontSize="sm" color="gray.500">
              Published on{" "}
              {new Date(newsItem.publishedDate).toLocaleDateString()}
            </Text>
          </Flex>
        </Box>
      </Box>
    </Container>
  );
}

export default NewsDetails;
